import type { MatchDetail } from '@brochure/shared'
import { cn } from '@/lib/utils'
import { MatchTimer } from './MatchTimer'
import { ScoreFlaps } from './ScoreFlaps'
import { ScrollingName } from '@/components/ui/ScrollingName'

type ScoreboardProps = { detail: MatchDetail; elapsed: number }
type Team = MatchDetail['homeTeam']

function TeamSide({ team, tone, align }: { team: Team; tone: 'sea' | 'coral'; align: 'left' | 'right' }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', align === 'right' ? 'items-end text-right' : 'items-start text-left')}>
      <span aria-hidden="true" className={cn('h-1 w-6 rounded-full', tone === 'sea' ? 'bg-sea' : 'bg-coral')} />
      <ScrollingName className="w-full font-body tabular-nums text-lg font-medium leading-tight text-paper sm:text-2xl">{team.name}</ScrollingName>
      <p className={cn('flex max-w-full flex-wrap gap-1', align === 'right' ? 'justify-end' : 'justify-start')}>
        {team.players.map((player) => (
          <span key={player.id} className={cn('truncate rounded-md px-1.5 py-0.5 font-body text-[0.7rem] text-paper/85', tone === 'sea' ? 'bg-sea/25' : 'bg-coral/25')}>
            {player.name}
          </span>
        ))}
      </p>
    </div>
  )
}

/** Il tabellone della partita: squadre ai lati (mare in casa, corallo in trasferta), punteggio a palette e cronometro. */
export function Scoreboard({ detail, elapsed }: ScoreboardProps) {
  const { match, homeTeam, awayTeam } = detail
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 sm:gap-6">
      <TeamSide team={homeTeam} tone="sea" align="right" />
      <div className="flex flex-col items-center gap-1.5">
        <span data-testid="score">
          <ScoreFlaps home={match.homeScore ?? 0} away={match.awayScore ?? 0} className="text-[2.25rem] text-paper sm:text-5xl" />
        </span>
        <MatchTimer elapsed={elapsed} running={match.timerRunning} />
      </div>
      <TeamSide team={awayTeam} tone="coral" align="left" />
    </div>
  )
}
